import styled, { DefaultTheme } from 'styled-components';
import { Card, Tabs } from 'antd';

declare module 'styled-components' {
  export interface DefaultTheme {
    mode?: 'light' | 'dark';
  }
}

/** Theme helper matching `Admin/MerchantEarningsOverview/Styles.ts`. */
const dark = (p: { theme?: DefaultTheme }, darkVal: string, lightVal: string): string =>
  p.theme?.mode === 'dark' ? darkVal : lightVal;

export const ACCENT = '#2D6CCA';

export const PageWrapper = styled.div`
  padding: 24px;
  background: ${(p) => dark(p, '#0f1419', '#f5f6f8')};
  min-height: 100vh;
`;

export const HeaderSection = styled.header`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 8px;
`;

export const HeaderLeft = styled.div`
  min-width: 0;
`;

export const PageTitle = styled.h1`
  margin: 0 0 4px 0;
  font-size: clamp(1.125rem, 2.5vw, 1.5rem);
  font-weight: 600;
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
`;

export const PageSubtitle = styled.p`
  margin: 0;
  font-size: 0.875rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
`;

export const TabDescription = styled.p`
  margin: 0 0 20px 0;
  font-size: 0.875rem;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
`;

export const StyledTabs = styled(Tabs)`
  margin-top: 20px;

  .ant-tabs-nav {
    background: ${(p) => dark(p, '#161b22', '#ffffff')};
    border-bottom: 1px solid ${(p) => dark(p, '#30363d', '#e8e8e8')};
    border-radius: 12px 12px 0 0;
    padding: 0 16px;
    margin-bottom: 0 !important;
  }

  .ant-tabs-tab {
    color: ${(p) => dark(p, '#8b949e', '#555')} !important;
    border-radius: 8px 8px 0 0 !important;
    margin-right: 8px;

    .ant-tabs-tab-btn {
      font-weight: 600;
    }

    &:hover {
      color: ${(p) => dark(p, '#c9d1d9', '#222')} !important;
    }
  }

  .ant-tabs-tab-active .ant-tabs-tab-btn {
    color: ${ACCENT} !important;
  }

  .ant-tabs-ink-bar {
    background: ${ACCENT} !important;
  }

  .ant-tabs-content {
    background: ${(p) => dark(p, '#161b22', '#ffffff')};
    border: 1px solid ${(p) => dark(p, '#30363d', '#e8e8e8')};
    border-top: none;
    border-radius: 0 0 12px 12px;
  }

  .ant-tabs-tabpane {
    padding: 24px;

    @media (max-width: 768px) {
      padding: 16px;
    }
  }
`;

/** Filter/search bar sitting above a table. */
export const TopControlBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`;

export const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
`;

export const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 12px;
`;

export const FilterLabel = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
`;

export const StatsRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 12px;
  margin-bottom: 20px;
`;

export const StatCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(p) => dark(p, '#30363d', '#e8e8e8')};
  background: ${(p) => dark(p, '#161b22', '#ffffff')};

  .ant-card-body {
    padding: 16px;
  }

  .ant-statistic-title {
    font-size: 0.75rem;
    color: ${(p) => dark(p, '#94a3b8', '#64748b')};
  }

  .ant-statistic-content {
    font-size: 1.25rem;
    color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
  }
`;

export const SectionCard = styled(Card)`
  border-radius: 12px;
  border: 1px solid ${(p) => dark(p, '#30363d', '#e8e8e8')};
  background: ${(p) => dark(p, '#161b22', '#ffffff')};
  margin-bottom: 20px;
`;

export const SectionTitle = styled.h2`
  margin: 0 0 4px 0;
  font-size: 1rem;
  font-weight: 600;
  color: ${(p) => dark(p, '#f1f5f9', '#0f172a')};
`;

/**
 * Caption for window-scoped figures (docs §3.2). These numbers cover only the
 * selected range and are not a live balance, so they never render unlabelled.
 */
export const WindowedCaption = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.7rem;
  font-weight: 500;
  color: ${(p) => dark(p, '#94a3b8', '#64748b')};
`;

/** Inline note used for documented API gaps we surface rather than hide. */
export const InlineNotice = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${(p) => dark(p, '#3b3d21', '#f0e6c0')};
  background: ${(p) => dark(p, '#22251a', '#fffbe6')};
  color: ${(p) => dark(p, '#d9d29a', '#7a6a2f')};
  font-size: 0.8rem;
  margin-bottom: 16px;
`;

export const PlaceholderPanel = styled.div`
  padding: 48px 16px;
  text-align: center;
  font-size: 0.875rem;
  color: ${(p) => dark(p, '#8b949e', '#94a3b8')};
`;

export const TableActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
